import {
  Box,
  Card,
  ComputedFieldProps,
  Form,
  FormRow,
  FormSection,
  InlineMessage,
  useLoadingStatusRead,
} from "@dbn/omnichannel-design-system-g4p";
import { CreditorData, ExternalLoanItemRework } from "../api";
import { getTypeOfProductLabel, RepaidLiabilityFormModel } from "../domain";
import { CreditorAutocompleteField } from "../Refinancing/CreditorAutocompleteField";
import { ReadOnlyMoneyAmountField } from "../../Common/ReadOnlyMoneyAmountField/ReadOnlyMoneyAmountField";
import { useFormatMessage } from "../../intl";
import { pipe } from "fp-ts/function";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { Option } from "fp-ts/Option";
import { option } from "fp-ts";
import { NonEmptyString } from "io-ts-types/lib/NonEmptyString";
import { CreditorConfig } from "../Refinancing/ExternalLiabilitiesEditForm";
import { Reader } from "fp-ts/Reader";
import { useReworkComparator } from "../Rework/useReworkComparator";
import { ReadOnlyFieldToValue } from "../../Common/ReadOnlyField/ReadOnlyFieldToValue";

type Props = {
  namePrefix: string;
  fieldProps: <K extends keyof RepaidLiabilityFormModel>(
    name: K
  ) => ComputedFieldProps<RepaidLiabilityFormModel[K]>;
  loanProviders: NonEmptyArray<CreditorData>;
  onCreditorNameChanged: Reader<NonEmptyString, unknown>;
  creditorConfig: CreditorConfig;
  oldValues: Option<ExternalLoanItemRework>;
};

export function RepaidLiabilitiesEdititableItem(props: Props) {
  const formatMessage = useFormatMessage();
  const { isSubmitting } = useLoadingStatusRead();

  const wrapper = pipe(
    props.oldValues,
    option.map(oldValues => ({ item: option.some(oldValues) }))
  );

  const { reworkFieldProps } = useReworkComparator(wrapper);

  const typeOfProductField = (
    <ReadOnlyFieldToValue
      label={formatMessage(
        "StandardLoan.Repaid.LiabilityItem.external.typeOfProductLabel"
      )}
      value={formatMessage(
        getTypeOfProductLabel(props.fieldProps("type").value)
      )}
      size="small"
    />
  );

  const originalAmountField = (
    <ReadOnlyMoneyAmountField
      label={formatMessage("StandardLoan.LiabilityItem.originalAmountLabel")}
      value={props.fieldProps("originalAmount").value}
      size="x-small"
    />
  );

  return (
    <Card>
      <Box column grow shrink>
        <Form>
          <FormSection>
            <FormRow type="1-1">
              {typeOfProductField}
              <CreditorAutocompleteField
                {...reworkFieldProps(
                  props.fieldProps("creditorName"),
                  "item",
                  isSubmitting,
                  "creditor"
                )}
                label={formatMessage("StandardLoan.RepaidLoans.creditorLabel")}
                placeholder={formatMessage(
                  "StandardLoan.RepaidLoans.creditorPlaceholder"
                )}
                onSelectSuggestion={suggestion => {
                  props.onCreditorNameChanged(suggestion.code);
                }}
                loanProviders={props.loanProviders}
              />
            </FormRow>
            <FormRow type="1-1">
              {originalAmountField}
              {null}
            </FormRow>
            <InlineMessage
              type="informative"
              size="medium"
              message={formatMessage(
                "StandardLoan.CBResults.Liabilities.repaidBeforeDisbursement"
              )}
            />
          </FormSection>
        </Form>
      </Box>
    </Card>
  );
}
